<template>
  <div v-if="current" class="block">
    <section v-if="showStrip" class="strip">
    <div v-if="current.status !== 'live' || showPushBell" class="top">
    <div v-if="current.status !== 'live'" class="lead">
      <svg class="cup" viewBox="0 0 64 64" aria-hidden="true">
        <path
          fill="currentColor"
          d="M22 8h20c1.1 0 2 .9 2 2v14c0 7.4-5 12.4-12 12.4S20 31.4 20 24V10c0-1.1.9-2 2-2zm-2 7h-4.2C11.2 15 8 18.4 8 22.8S11.2 30.6 15.8 30.6H20v-4h-3.4c-2.2 0-4-1.7-4-3.8s1.8-3.8 4-3.8H20v-4zm24 0v4h3.4c2.2 0 4 1.7 4 3.8s-1.8 3.8-4 3.8H44v4h4.2C52.8 30.6 56 27.2 56 22.8S52.8 15 48.2 15H44zM28 38h8v8h-8zM18 48h28c1.1 0 2 .9 2 2v2c0 1.1-.9 2-2 2H18c-1.1 0-2-.9-2-2v-2c0-1.1.9-2 2-2z"
        />
        <path
          class="star"
          d="M32 14.2l2.1 4.4 4.8.6-3.5 3.3.9 4.7L32 24.8l-4.3 2.4.9-4.7-3.5-3.3 4.8-.6z"
        />
      </svg>
      <div class="copy">
        <p v-if="!schedulePaused" class="timer">{{ countdown }}</p>
        <p v-if="!schedulePaused" class="wait">{{ waitLead }}</p>
        <p class="status">
          <span class="sizer" aria-hidden="true">{{ t('tournament.waitJoin') }}</span>
          <span class="sizer" aria-hidden="true">{{ t('tournament.joinWindow') }}</span>
          <span class="sizer" aria-hidden="true">{{ t('tournament.joined') }}</span>
          <span class="sizer" aria-hidden="true">{{ t('tournament.closedSoon') }}</span>
          <span class="sizer" aria-hidden="true">{{ t('tournament.autosPaused') }}</span>
          <span>{{ statusLine }}</span>
        </p>
      </div>
    </div>
    <button
      v-if="showPushBell"
      type="button"
      class="bell"
      :class="{ off: pushArmed }"
      :aria-label="pushLabel"
      :title="pushLabel"
      @click="onBellClick"
    >
      <svg v-if="pushArmed" class="bell-icon" viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="currentColor"
          d="M20 18.69 7.84 6.14 5.27 3.49 4 4.76l2.8 2.8v.01C6.28 8.56 6 9.73 6 10.95v5l-2 2v1h13.73l2 2L21 19.27zM12 22c1.11 0 2-.89 2-2h-4c0 1.11.89 2 2 2zm6-7.32V11c0-3.08-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68c-.24.06-.47.14-.69.23L18 14.68z"
        />
      </svg>
      <svg v-else class="bell-icon" viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="currentColor"
          d="M12 22a2.4 2.4 0 0 0 2.35-2h-4.7A2.4 2.4 0 0 0 12 22zm7-6.2V11c0-3.4-1.8-6.2-4.9-7V3.2a2.1 2.1 0 0 0-4.2 0V4C6.8 4.8 5 7.6 5 11v4.8L3.3 17.5V19h17.4v-1.5L19 15.8z"
        />
      </svg>
    </button>
    </div>
    <p v-if="pushNote" class="push-note">{{ pushNote }}</p>

    <div v-if="current.status === 'registration'" class="roster">
      <span class="seats" aria-hidden="true">
        <span
          v-for="seat in seatCount"
          :key="seat"
          class="seat"
          :class="{ on: seat <= signedUp, mine: joined && seat === signedUp }"
        />
      </span>
      <span class="roster-label">
        {{ t('tournament.registered', { n: signedUp, max: seatCount }) }}
      </span>
    </div>

    <div class="actions">
      <button
        v-if="current.status === 'registration' && (joined || canJoin)"
        type="button"
        class="btn btn-slot"
        :class="joined ? 'btn-line' : 'btn-fill'"
        :disabled="busy"
        @click="joined ? leave() : join()"
      >
        <span class="sizer" aria-hidden="true">{{ t('tournament.join') }}</span>
        <span class="sizer" aria-hidden="true">{{ t('tournament.leave') }}</span>
        <span>{{ joined ? t('tournament.leave') : t('tournament.join') }}</span>
      </button>
      <router-link v-if="showHistory" to="/tournaments" class="btn btn-line">
        {{ t('tournament.history') }}
      </router-link>
    </div>
    <p v-if="autoDeckName" class="wait">
      {{ t('tournament.autoJoined', { name: autoDeckName }) }}
    </p>
    <p v-if="errorText" class="error">{{ errorText }}</p>
    </section>
    <TournamentBracket
      v-if="bracketMatches.length"
      class="board"
      :matches="bracketMatches"
      :me="auth.user?.id ?? null"
      @spectate="openSpectate"
      @play="(id) => openTournamentMatch(id, 'bracket')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import {
  TOURNAMENT_CAPACITY_DEFAULT,
  TOURNAMENT_JOIN_CUTOFF_MS,
  type TournamentMatchView,
} from '@shared/index'
import TournamentBracket from './TournamentBracket.vue'
import { useI18n } from '../i18n/t'
import {
  askTournamentPush,
  enableTournamentPush,
  muteTournamentPush,
  pushMuted,
  pushSupported,
} from '../push/arm'
import { useAuthStore } from '../stores/auth'
import { useTournamentStore } from '../stores/tournament'
import { openSpectate, openTournamentMatch } from '../tournament/go'

const { t } = useI18n()
const route = useRoute()
const auth = useAuthStore()
const tournament = useTournamentStore()
const { current, joined, busy, error, autoDeckName } = storeToRefs(tournament)
const now = ref(Date.now())
let timer: ReturnType<typeof setInterval> | null = null
let pushWatch: PermissionStatus | null = null

type PushPerm = NotificationPermission | 'unsupported'

function readPushPerm(): PushPerm {
  if (!pushSupported()) return 'unsupported'
  return Notification.permission
}

const pushPerm = ref<PushPerm>(readPushPerm())
const muted = ref(pushMuted())

const pushArmed = computed(
  () => pushPerm.value === 'granted' && !muted.value,
)

const showPushBell = computed(() => pushPerm.value !== 'unsupported')

function phoneBrowser(): boolean {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
}

function deniedNote(): string {
  return phoneBrowser() ? t('settings.pushDenied') : t('tournament.pushDeniedBrowser')
}

const pushLabel = computed(() => {
  if (pushPerm.value === 'denied') return deniedNote()
  if (pushArmed.value) return t('settings.pushDisable')
  return t('settings.pushEnable')
})

const pushNote = ref('')

function refreshPushPerm(): void {
  pushPerm.value = readPushPerm()
  muted.value = pushMuted()
  if (pushArmed.value) pushNote.value = ''
}

function onBellClick(): void {
  if (pushArmed.value) {
    void muteTournamentPush().then(() => {
      muted.value = true
      pushNote.value = ''
    })
    return
  }
  if (pushPerm.value === 'denied') {
    pushNote.value = deniedNote()
    return
  }
  if (pushPerm.value !== 'granted' && !phoneBrowser()) {
    pushNote.value = t('tournament.pushPrompt')
  }
  void enableTournamentPush().then((perm) => {
    pushPerm.value = perm
    muted.value = pushMuted()
    if (perm === 'granted') {
      pushNote.value = ''
      return
    }
    if (perm === 'denied') {
      pushNote.value = deniedNote()
      return
    }
    if (!phoneBrowser()) pushNote.value = t('tournament.pushPrompt')
  })
}

onMounted(() => {
  timer = setInterval(() => {
    now.value = Date.now()
  }, 1000)
  window.addEventListener('focus', refreshPushPerm)
  if (!pushSupported() || !navigator.permissions?.query) return
  void navigator.permissions
    .query({ name: 'notifications' as PermissionName })
    .then((status) => {
      pushWatch = status
      refreshPushPerm()
      status.onchange = refreshPushPerm
    })
    .catch(() => undefined)
})

onUnmounted(() => {
  if (timer) clearInterval(timer)
  window.removeEventListener('focus', refreshPushPerm)
  if (pushWatch) pushWatch.onchange = null
})

const remainingMs = computed(() => {
  const start = current.value ? Date.parse(current.value.startsAt) : now.value
  return Math.max(0, start - now.value)
})

const remainingSec = computed(() => Math.floor(remainingMs.value / 1000))

const countdown = computed(() => {
  const left = remainingSec.value
  const minutes = Math.floor(left / 60)
  const seconds = left % 60
  const hours = Math.floor(minutes / 60)
  if (hours > 0) {
    return `${hours}:${String(minutes % 60).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  }
  return `${minutes}:${String(seconds).padStart(2, '0')}`
})

const seatCount = computed(() => {
  const n = current.value?.capacity
  return typeof n === 'number' && n > 0 ? n : TOURNAMENT_CAPACITY_DEFAULT
})

const signedUp = computed(() => {
  const n = current.value?.registered
  const count = typeof n === 'number' ? n : 0
  return Math.max(0, Math.min(count, seatCount.value))
})

const canJoin = computed(() => remainingMs.value > TOURNAMENT_JOIN_CUTOFF_MS)

const schedulePaused = computed(
  () => Boolean(current.value?.autosPaused) && current.value?.status === 'upcoming',
)

const statusLine = computed(() => {
  if (schedulePaused.value) return t('tournament.autosPaused')
  if (joined.value) return t('tournament.joined')
  if (current.value?.status === 'upcoming') return t('tournament.joinWindow')
  if (current.value?.status === 'registration' && !canJoin.value) {
    return t('tournament.closedSoon')
  }
  return t('tournament.waitJoin')
})

const waitLead = computed(() => {
  const totalMinutes = Math.floor(remainingSec.value / 60)
  const hours = Math.floor(totalMinutes / 60)
  if (hours > 1) return t('tournament.waitHours', { n: hours })
  if (hours === 1) return t('tournament.waitHour')
  if (totalMinutes > 1) return t('tournament.waitMinutes', { n: totalMinutes })
  if (totalMinutes === 1) return t('tournament.waitMinute')
  return t('tournament.waitSoon')
})

const showHistory = computed(
  () => route.name !== 'tournaments' && route.name !== 'tournament-detail',
)

const showStrip = computed(() => {
  const live = current.value
  if (!live) return false
  if (live.status !== 'live') return true
  return showHistory.value || Boolean(error.value)
})

const bracketMatches = computed((): TournamentMatchView[] => {
  const live = current.value
  if (!live) return []
  if (live.status === 'live') return live.matches ?? []
  return live.sampleLive?.matches ?? []
})

const errorText = computed(() => {
  if (!error.value) return ''
  const key = `tournament.errors.${error.value}`
  const text = t(key)
  return text === key ? t('tournament.errors.deck') : text
})

function join(): void {
  void askTournamentPush()
  tournament.openPicker()
}

function leave(): void {
  void tournament.leave()
}
</script>

<style scoped>
.block {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0 0 24px;
  padding: 16px;
  border-radius: 16px;
  background: var(--md-sys-color-surface-container);
}

.board {
  width: 100%;
}

.strip {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  margin: 0;
  padding: 16px;
  border-radius: 12px;
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
}

.top {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.lead {
  display: flex;
  flex: 1;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.bell {
  display: inline-flex;
  flex: 0 0 44px;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin-left: auto;
  padding: 0;
  border: 0;
  border-radius: 22px;
  background: var(--md-sys-color-surface);
  color: var(--md-sys-color-primary);
  cursor: pointer;
}

.bell:hover {
  background: color-mix(in srgb, var(--md-sys-color-surface) 88%, var(--md-sys-color-primary));
}

.bell.off {
  color: var(--md-sys-color-on-surface-variant);
}

.bell-icon {
  width: 24px;
  height: 24px;
}

.push-note {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.35;
}

.cup {
  flex: 0 0 56px;
  width: 56px;
  height: 56px;
}

.star {
  fill: var(--md-sys-color-primary-container);
}

.copy {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.timer {
  margin: 0;
  font-size: 40px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.03em;
  line-height: 1;
}

.wait,
.status {
  margin: 0;
  max-width: 36em;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.35;
}

.status {
  display: grid;
  width: 100%;
}

.status > span,
.btn-slot > span {
  grid-area: 1 / 1;
  min-width: 0;
}

.sizer {
  visibility: hidden;
}

.roster {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  align-self: flex-start;
  max-width: 100%;
  gap: 12px;
  min-height: 40px;
  padding: 6px 14px 6px 10px;
  border-radius: 999px;
  background: var(--md-sys-color-surface);
  color: var(--md-sys-color-on-surface);
}

.seats {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  max-width: 150px;
}

.seat {
  width: 12px;
  height: 12px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--md-sys-color-on-surface) 22%, transparent);
}

.seat.on {
  background: var(--md-sys-color-primary);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--md-sys-color-primary) 32%, transparent);
}

.seat.mine {
  box-shadow:
    0 0 0 2px var(--md-sys-color-surface),
    0 0 0 4px var(--md-sys-color-primary);
}

.roster-label {
  font-size: 14px;
  font-weight: 650;
  letter-spacing: 0.01em;
}

.error {
  margin: 0;
  font-size: 14px;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-height: 44px;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 12px;
  border: 2px solid transparent;
  font: inherit;
  font-size: 15px;
  font-weight: 650;
  text-decoration: none;
  cursor: pointer;
}

.btn-slot {
  display: inline-grid;
  place-items: center;
  white-space: nowrap;
}

.btn-fill {
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
}

.btn-line {
  background: var(--md-sys-color-surface);
  color: var(--md-sys-color-on-surface);
  border-color: var(--md-sys-color-outline);
}

.btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.error {
  flex-basis: 100%;
}
</style>
